import actionTypes from '@constants/actionTypes'
import {TimePicker} from 'antd'
import {Image} from 'assets/images/Images/Image'
import InputDateFormik from 'components/atom/Inputs/InputDateFormik'
import LabelTitle from 'components/atom/Labels/LabelTitle'
import BackGroundSVG from 'components/atom/SVG/BackGroundSVG'
import CommonSVG from 'components/atom/SVG/CommonSVG'
import ModalLayout from 'components/modal/ModalLayout'
import When from 'components/when/When'
import {useFormik} from 'formik'
import {useEffect, useMemo, useState} from 'react'
import {ActionItem} from 'screens/Patients/LeadsProfile/leadsProfile.types'
import {SVG_CLOUD, SVG_CORRECT_GREEN, SVG_CROSS, SVG_SWITCH_ARROW} from 'utils/SvgConstants'
import hasValue from 'utils/hasValue'
import * as Yup from 'yup'
import {
  disabledTime,
  getFirstLetterCapitalOfWord,
  getImageUrl,
  openDocument,
} from 'utils/ConstFunctions'
import validateAndProcessPhotos from 'screens/Patients/Chat/helpers/checkPhotosValidation'
import fileFormatType from '@staticData/fileFormatType'
import pdfPng from 'assets/images/Pdf.png'
import mp4Png from 'assets/images/mp4.png'
import {useDispatch, useSelector} from 'react-redux'
import {RootState} from 'redux/store'
import ImageViewer from 'screens/Patients/PatientProfile/Tabs/ImageViewer'
import Button from 'components/atom/Buttons/Button'
import moment from 'moment'
import {
  setDataForceAlignerData,
  IForceAlignerProps,
} from 'redux/Slices/AppSlice/LeadsProfile/AlignerTracking.slice'
import useSubscriptionDetails from '@hooks/useSubscriptionDetails'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import PDFWebview from '../../files/components/PDFWebview'

dayjs.extend(utc)
dayjs.extend(timezone)

interface IForceAlignerData {
  handleOnClose: (option: ActionItem) => void
  handleActionOnClick: (option: ActionItem) => void
  treatmentDetail?: AlignerJourneyData | null
  alignerJourneyId?: number | string | null
}

const schema = Yup.object().shape({
  changeDate: Yup.string().required('Please select a value'),
  changeTime: Yup.string().required('Please select a value'),
})

const initialValues = {
  changeTime: dayjs().tz('Asia/Kolkata').format('h:mm A'),
  changeDate: '',
  files: [],
}
const defaultMinChangeDate = '2024-01-11'

interface FormValues {
  changeTime: string
  changeDate: string
  files: File[]
}

type AlignerNumberValue = number | string | null | undefined

interface AlignerData {
  sr_no?: AlignerNumberValue
  aligner_number?: AlignerNumberValue
  current_aligner_number?: AlignerNumberValue
  total_aligner?: AlignerNumberValue
  aligner_journey_id?: AlignerNumberValue
  jaw_type?: string
  current_aligner_jaw_type?: string
  start_date?: string
}

interface AlignerJourneyData {
  id?: AlignerNumberValue
  aligner_journey_id?: AlignerNumberValue
  journey_id?: AlignerNumberValue
  aligner_treatment_id?: AlignerNumberValue
  current_aligner_no?: AlignerNumberValue
  total_aligners?: AlignerNumberValue
  aligners?: AlignerData[]
}

interface PatientTimelineData {
  aligners?: AlignerData[]
  patient_profile_overview_response?: {
    current_aligner?: AlignerNumberValue
    total_aligner?: AlignerNumberValue
  }
}

interface AlignerChangeDetails {
  startJawType: string
  endJawType: string
  startAlignerNumber: number
  endAlignerNumber: number
  minChangeDate: string
  alignerJourneyId?: number
}

const getNumberValue = (value: AlignerNumberValue) => {
  if (value === null || value === undefined || value === '') return null

  const parsedValue = Number(value)
  return Number.isFinite(parsedValue) ? parsedValue : null
}

const getAlignerNumber = (aligner?: AlignerData | null) => {
  return getNumberValue(aligner?.sr_no ?? aligner?.aligner_number)
}

const getFormattedJawType = (jaw?: string | null) => {
  return getFirstLetterCapitalOfWord(jaw ?? '')
}

const getAlignerMinChangeDate = (aligner?: AlignerData | null) => {
  return aligner?.start_date
    ? moment(aligner.start_date).add(1, 'days').format('YYYY-MM-DD')
    : defaultMinChangeDate
}

const getJourneyNumber = (
  treatmentDetail: AlignerJourneyData | null | undefined,
  alignerJourneyId: AlignerNumberValue
) => {
  return getNumberValue(
    treatmentDetail?.aligner_journey_id ??
      treatmentDetail?.journey_id ??
      treatmentDetail?.id ??
      alignerJourneyId
  )
}

const isCompleteAlignerChangeDetails = (details: AlignerChangeDetails | null) => {
  return !!details && details.startAlignerNumber > 0 && details.endAlignerNumber > 0
}

const hasReadableAlignerChangeDetails = (details: AlignerChangeDetails | null) => {
  return isCompleteAlignerChangeDetails(details) && (details?.startJawType || details?.endJawType)
}

const hasCurrentAlignerDetails = (details: AlignerChangeDetails | null) => {
  return !!details && details.startAlignerNumber > 0
}

const resolveAlignerJourney = (
  dataTreatmentPlan: {aligner_journeys?: AlignerJourneyData[]} | null,
  treatmentDetail: AlignerJourneyData | null | undefined,
  alignerJourneyId: AlignerNumberValue
) => {
  if (treatmentDetail) return treatmentDetail

  const alignerJourneys = dataTreatmentPlan?.aligner_journeys ?? []
  const selectedJourneyId = getNumberValue(alignerJourneyId)

  if (selectedJourneyId !== null) {
    const matchingJourney = alignerJourneys.find((journey) =>
      [
        journey.aligner_journey_id,
        journey.journey_id,
        journey.aligner_treatment_id,
        journey.id,
      ].some((journeyId) => getNumberValue(journeyId) === selectedJourneyId)
    )

    if (matchingJourney) return matchingJourney
  }

  return alignerJourneys[0] ?? null
}

const getAlignerChangeDetailsFromJourney = (
  treatmentDetail: AlignerJourneyData | null,
  alignerJourneyId: AlignerNumberValue
): AlignerChangeDetails | null => {
  if (!hasValue(treatmentDetail)) return null

  const currentAlignerNumber = getNumberValue(treatmentDetail?.current_aligner_no)
  if (currentAlignerNumber === null) return null

  const sortedAligners = [...(treatmentDetail?.aligners ?? [])].sort(
    (firstAligner, secondAligner) =>
      (getAlignerNumber(firstAligner) ?? 0) - (getAlignerNumber(secondAligner) ?? 0)
  )
  const currentAligner =
    sortedAligners.find((aligner) => getAlignerNumber(aligner) === currentAlignerNumber) ?? null
  const nextAligner =
    sortedAligners.find((aligner) => {
      const alignerNumber = getAlignerNumber(aligner)
      return alignerNumber !== null && alignerNumber > currentAlignerNumber
    }) ?? null
  const nextAlignerNumber = getAlignerNumber(nextAligner)
  const totalAligners = getNumberValue(treatmentDetail?.total_aligners)
  const fallbackNextAlignerNumber =
    totalAligners !== null && currentAlignerNumber < totalAligners ? currentAlignerNumber + 1 : 0
  const currentJawType = currentAligner?.jaw_type ?? ''

  return {
    startJawType: getFormattedJawType(currentJawType),
    endJawType: getFormattedJawType(nextAligner?.jaw_type ?? currentJawType),
    startAlignerNumber: getAlignerNumber(currentAligner) ?? currentAlignerNumber,
    endAlignerNumber: nextAlignerNumber ?? fallbackNextAlignerNumber,
    minChangeDate: getAlignerMinChangeDate(currentAligner),
    alignerJourneyId: getJourneyNumber(treatmentDetail, alignerJourneyId) ?? undefined,
  }
}

const getAlignerChangeDetailsFromTimeline = (
  patientTimeline: PatientTimelineData | null | undefined,
  alignerJourneyId: AlignerNumberValue
): AlignerChangeDetails | null => {
  const sortedAligners = [...(patientTimeline?.aligners ?? [])].sort(
    (firstAligner, secondAligner) =>
      (getAlignerNumber(firstAligner) ?? 0) - (getAlignerNumber(secondAligner) ?? 0)
  )
  const currentAlignerFromRows =
    sortedAligners.find((aligner) => {
      const alignerNumber = getAlignerNumber(aligner)
      const currentAlignerNumber = getNumberValue(aligner.current_aligner_number)

      return (
        alignerNumber !== null &&
        currentAlignerNumber !== null &&
        alignerNumber === currentAlignerNumber
      )
    }) ?? null
  const currentAlignerNumber =
    getNumberValue(patientTimeline?.patient_profile_overview_response?.current_aligner) ??
    getNumberValue(currentAlignerFromRows?.current_aligner_number)

  if (currentAlignerNumber === null) return null

  const currentAligner =
    sortedAligners.find((aligner) => getAlignerNumber(aligner) === currentAlignerNumber) ??
    currentAlignerFromRows
  const nextAligner =
    sortedAligners.find((aligner) => {
      const alignerNumber = getAlignerNumber(aligner)
      return alignerNumber !== null && alignerNumber > currentAlignerNumber
    }) ?? null
  const nextAlignerNumber = getAlignerNumber(nextAligner)
  const totalAligners =
    getNumberValue(currentAligner?.total_aligner) ??
    getNumberValue(patientTimeline?.patient_profile_overview_response?.total_aligner)
  const fallbackNextAlignerNumber =
    totalAligners !== null && currentAlignerNumber < totalAligners ? currentAlignerNumber + 1 : 0
  const currentJawType = currentAligner?.jaw_type ?? currentAligner?.current_aligner_jaw_type ?? ''

  return {
    startJawType: getFormattedJawType(currentJawType),
    endJawType: getFormattedJawType(nextAligner?.jaw_type ?? currentJawType),
    startAlignerNumber: getAlignerNumber(currentAligner) ?? currentAlignerNumber,
    endAlignerNumber: nextAlignerNumber ?? fallbackNextAlignerNumber,
    minChangeDate: getAlignerMinChangeDate(currentAligner),
    alignerJourneyId:
      getNumberValue(currentAligner?.aligner_journey_id ?? nextAligner?.aligner_journey_id) ??
      getNumberValue(alignerJourneyId) ??
      undefined,
  }
}

const ForceAlignerChange = (props: IForceAlignerData) => {
  const {handleOnClose, handleActionOnClick, treatmentDetail, alignerJourneyId} = props
  const dispatch = useDispatch()
  const [fileUrls, setFileUrls] = useState<Array<{url: string; type: string}>>([])
  const [minChangeDate, setMinChangeDate] = useState(defaultMinChangeDate)
  const [startJawType, setStartJawType] = useState('')
  const [endJawType, setEndJawType] = useState('')
  const [startAlignerNumber, setStartAlignerNumber] = useState(0)
  const [endAlignerNumber, setEndAlignerNumber] = useState(0)
  const [selectedAlignerJourneyId, setSelectedAlignerJourneyId] = useState<number | undefined>()
  const [isShowPhotos, setIsShowPhotos] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const {loading: loadingTreatmentPlan, data: dataTreatmentPlan}: any = useSelector(
    (state: RootState) => state.apiTreatmentPlan
  )
  const selectedTreatmentDetail = useMemo(
    () => resolveAlignerJourney(dataTreatmentPlan, treatmentDetail, alignerJourneyId),
    [alignerJourneyId, dataTreatmentPlan, treatmentDetail]
  )
  const {patientTimeline} = useSelector((state: RootState) => state.leadsProfile)
  const resolvedAlignerChangeDetails = useMemo(() => {
    const treatmentPlanDetails = getAlignerChangeDetailsFromJourney(
      selectedTreatmentDetail,
      alignerJourneyId
    )
    const timelineDetails = getAlignerChangeDetailsFromTimeline(patientTimeline, alignerJourneyId)

    if (hasReadableAlignerChangeDetails(timelineDetails)) return timelineDetails
    if (hasCurrentAlignerDetails(timelineDetails)) return timelineDetails
    if (hasReadableAlignerChangeDetails(treatmentPlanDetails)) return treatmentPlanDetails
    if (isCompleteAlignerChangeDetails(treatmentPlanDetails)) return treatmentPlanDetails

    return timelineDetails ?? treatmentPlanDetails
  }, [alignerJourneyId, patientTimeline, selectedTreatmentDetail])
  const displayStartJawType = resolvedAlignerChangeDetails?.startJawType ?? startJawType
  const displayEndJawType = resolvedAlignerChangeDetails?.endJawType ?? endJawType
  const displayStartAlignerNumber =
    resolvedAlignerChangeDetails?.startAlignerNumber ?? startAlignerNumber
  const displayEndAlignerNumber = resolvedAlignerChangeDetails?.endAlignerNumber ?? endAlignerNumber
  const displayMinChangeDate = resolvedAlignerChangeDetails?.minChangeDate ?? minChangeDate
  const currentAlignerJourneyId =
    resolvedAlignerChangeDetails?.alignerJourneyId ?? selectedAlignerJourneyId
  const [pdfViewer, setPdfViewer] = useState<{
    isOpen: boolean
    url: string
    fileName?: string
  }>({
    isOpen: false,
    url: '',
    fileName: '',
  })

  // PDF handler functions - Add these
  const handleOpenPDF = (url: string, fileName?: string) => {
    setPdfViewer({
      isOpen: true,
      url,
      fileName,
    })
  }

  const handleClosePDF = () => {
    setPdfViewer({
      isOpen: false,
      url: '',
      fileName: '',
    })
  }
  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema: schema,
    onSubmit: async (values) => {
      const now = dayjs()
      const selectedDateTime = dayjs(
        `${dayjs().format('YYYY-MM-DD')} ${values.changeTime}`,
        'YYYY-MM-DD h:mm A'
      )

      if (selectedDateTime.isBefore(now)) {
        values.changeTime = now.format('h:mm A')
        formik.setFieldValue('changeTime', values.changeTime)
      }

      if (displayStartAlignerNumber <= 0 || displayEndAlignerNumber <= 0) return

      if (values.changeDate && values.changeTime) {
        dispatch(
          setDataForceAlignerData({
            startJawType: displayStartJawType,
            endJawType: displayEndJawType,
            startAlignerNumber: displayStartAlignerNumber,
            endAlignerNumber: displayEndAlignerNumber,
            changeDate: values.changeDate,
            changeTime: values.changeTime,
            alignerJourneyId: currentAlignerJourneyId,
            files: values.files,
          })
        )

        if (dayjs(values.changeDate).isSame(now, 'day')) {
          handleActionOnClick(actionTypes.FORCE_CHANGE_ALIGNER_CONFIRM_MODAL)
        } else {
          handleActionOnClick(actionTypes.FORCE_CHANGE_ALIGNER_WARNING_MODAL)
        }
      }
    },
  })

  useEffect(() => {
    if (loadingTreatmentPlan && !resolvedAlignerChangeDetails) return

    if (!hasValue(resolvedAlignerChangeDetails)) {
      setStartJawType('')
      setEndJawType('')
      setStartAlignerNumber(0)
      setEndAlignerNumber(0)
      setSelectedAlignerJourneyId(undefined)
      setMinChangeDate(defaultMinChangeDate)
      return
    }

    setStartJawType(resolvedAlignerChangeDetails.startJawType)
    setEndJawType(resolvedAlignerChangeDetails.endJawType)
    setStartAlignerNumber(resolvedAlignerChangeDetails.startAlignerNumber)
    setEndAlignerNumber(resolvedAlignerChangeDetails.endAlignerNumber)
    setSelectedAlignerJourneyId(resolvedAlignerChangeDetails.alignerJourneyId)
    setMinChangeDate(resolvedAlignerChangeDetails.minChangeDate)
  }, [loadingTreatmentPlan, resolvedAlignerChangeDetails])

  // const onChange: TimePickerProps['onChange'] = (time, timeString) => {
  //   formik.setFieldValue('changeTime', timeString)
  // }

  const handleRemoveFile = (index: number) => {
    const newFiles = [...formik.values.files]
    newFiles.splice(index, 1)
    formik.setFieldValue('files', newFiles)

    const newFileUrls = [...fileUrls]
    newFileUrls.splice(index, 1)
    setFileUrls(newFileUrls)
  }
  const {subscriptionData} = useSubscriptionDetails()
  const canChangeAligner = displayStartAlignerNumber > 0 && displayEndAlignerNumber > 0

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.files && event.currentTarget.files?.length > 0) {
      const validatedFileList = validateAndProcessPhotos({
        files: event.currentTarget.files,
        fileCount: 6,
        toastMessage: 'You can upload a maximum of 5 files at a time',
        chatFileFormats: fileFormatType.TREATMENT_PLAN_EXTENSIONS,
        maxFileSize: 50,
        availableStorage: subscriptionData?.total_storage_gb,
        usedStorage: subscriptionData?.used_storage_gb,
      })
      const files = Array.from(validatedFileList)
      formik.setFieldValue('files', files)

      const urls = files.map((file) => ({
        url: URL.createObjectURL(file),
        type: file.type,
        name: file.name,
      }))
      setFileUrls(urls)
    }
  }

  return (
    <>
      {pdfViewer.isOpen && (
        <PDFWebview pdfUrl={pdfViewer.url} onBack={handleClosePDF} fileName={pdfViewer.fileName} />
      )}
      {!pdfViewer.isOpen && (
        <ModalLayout isResponsive className='md:!w-[45rem] max-h-[95%] overflow-auto'>
          <When isTrue={isShowPhotos}>
            <ImageViewer
              setIsShowPhotos={setIsShowPhotos}
              selectedImagesList={fileUrls
                .filter((file) => file.type !== 'application/pdf')
                .map((file, index) => ({
                  id: index,
                  src: getImageUrl(file),
                  width: '100%',
                  height: '100%',
                }))}
              selectedIndex={selectedIndex}
            />
          </When>
          <form className='p-3' onSubmit={formik.handleSubmit}>
            <div className='flex justify-end items-center mb-2'></div>
            <div className='max-h-[70vh] overflow-y-auto flex justify-between'>
              <div>
                <div className='font-semibold text-[24px] mt-'>Manual aligner change</div>
                <div className='font-[16px]'>
                  Log an aligner change done in person or not updated by the patient.{' '}
                </div>
              </div>
              <div
                className='cursor-pointer'
                onClick={() => {
                  handleOnClose(actionTypes.FORCE_CHANGE_ALIGNER)
                }}
              >
                <CommonSVG svg={SVG_CROSS} width='47' height='47' />
              </div>
            </div>
            <InstructionBoard />
            <AlignerSwitch
              startJawType={displayStartJawType}
              endJawType={displayEndJawType}
              endAlignerNumber={displayEndAlignerNumber}
              startAlignerNumber={displayStartAlignerNumber}
            />

            <div className='w-full flex mt-7 gap-4'>
              <div className='w-1/2'>
                <LabelTitle required={true} title='Change date' className='' />
                <InputDateFormik
                  {...{
                    name: 'changeDate',
                    label: '',
                    className: ' py-3',
                    required: false,
                    minDate: dayjs(displayMinChangeDate),
                    maxDate: dayjs(),
                    onChange: (date: dayjs.Dayjs, dateString: string | string[]) => {
                      formik?.setFieldValue('changeDate', dateString)
                    },
                  }}
                  dateValue={formik?.values?.changeDate}
                  formik={formik}
                />
              </div>
              <div className='w-1/2'>
                <LabelTitle required={true} title='Change time' className='' />

                <TimePicker
                  name='changeTime'
                  use12Hours
                  format='h:mm A'
                  onChange={(time) => {
                    formik.setFieldValue('changeTime', time ? time.format('h:mm A') : null)
                  }}
                  value={
                    formik.values.changeTime ? dayjs(formik.values.changeTime, 'h:mm A') : null
                  }
                  size='large'
                  className='custom-time-input w-full px-2 h-12 rounded-lg border border-mediumGray z-4'
                  disabledTime={disabledTime}
                />
                <div className='text-xs text-red mt-1'>
                  {formik.touched.changeTime && formik.errors.changeTime && (
                    <div className='text-red'>{formik.errors.changeTime}</div>
                  )}
                </div>
              </div>
            </div>

            <div className='mt-6'>
              <div className='flex justify-between mb-2 items-center'>
                <div>
                  <LabelTitle
                    required={false}
                    title='Upload photos (Optional)'
                    className='text-[18px] font-medium text-textColor'
                  />
                  <div className='text-[14px] font-medium text-textColor'>
                    (The photos will be visible in the{' '}
                    {displayStartJawType + ' ' + displayStartAlignerNumber} folder)
                  </div>
                </div>
                <When isTrue={fileUrls?.length > 0}>
                  <button
                    type='button'
                    className='text-secondaryColor text-sm font-medium text-right'
                    onClick={() => {
                      formik.setFieldValue('files', [])
                      setFileUrls([])
                    }}
                  >
                    Clear all
                  </button>
                </When>
              </div>
              <When isTrue={!hasValue(fileUrls)}>
                <div className='h-[90px] bg-primarySupport flex justify-center items-center cursor-pointer relative border border-primaryColor rounded-lg'>
                  <input
                    style={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: 0,
                      right: 0,
                      opacity: 0,
                    }}
                    type='file'
                    id='image'
                    multiple
                    accept='.jpg, .jpeg, .png'
                    onChange={handleFileChange}
                    className='cursor-pointer'
                  />
                  <label
                    htmlFor='image'
                    className='p-3 cursor-pointer flex-col justify-center items-center'
                  >
                    <div className='flex justify-center items-center gap-2'>
                      <CommonSVG svg={SVG_CLOUD} width='24' height='19' />
                      <p className='text-base font-semibold text-primaryColor'>
                        Tap to upload files
                      </p>
                    </div>
                  </label>
                </div>
              </When>
              <When isTrue={hasValue(fileUrls)}>
                <div className='flex flex-wrap w-full gap-8 justify-between '>
                  {fileUrls?.map((file, index) => (
                    <div
                      key={index}
                      className='flex items-center w-[42.5%] gap-2 flex-shrink justify-between'
                    >
                      <div className='flex items-center justify-between gap-4'>
                        <When isTrue={file.type !== 'application/pdf' && file.type !== 'video/mp4'}>
                          <Image
                            src={file.url ?? ''}
                            alt='Uploaded file '
                            className='w-12 h-12 rounded-[4px] border border-lightGray object-cover cursor-pointer'
                            onClick={() => {
                              setIsShowPhotos(true)
                              setSelectedIndex(index)
                            }}
                            size={20}
                            fileName={formik.values.files[index]?.name}
                            showFileName={true}
                            showLoading={true}
                          />
                        </When>
                        <When isTrue={file.type === 'video/mp4'}>
                          <Image
                            className='w-12 h-12 rounded-[4px] object-cover cursor-pointer'
                            onClick={() => openDocument(file?.url ?? '')}
                            size={20}
                            src={mp4Png}
                          />
                          <p className='break-all'>{formik.values.files[index].name} </p>
                        </When>
                        <When isTrue={file.type === 'application/pdf'}>
                          <Image
                            className='w-12 h-12 rounded-[4px]  object-cover cursor-pointer'
                            onClick={() => handleOpenPDF(file.url ?? '')}
                            size={20}
                            src={pdfPng}
                          />

                          <p className='break-all'>{formik.values.files[index].name} </p>
                        </When>
                      </div>
                      <div
                        className='cursor-pointer'
                        onClick={() => {
                          handleRemoveFile(index)
                        }}
                      >
                        <CommonSVG svg={SVG_CROSS} width='40' height='40' />
                      </div>
                    </div>
                  ))}
                </div>
              </When>
            </div>
            <div className='mt-8 w-full'>
              <Button
                className='bg-primaryColor text-white font-semibold text-base w-full h-[56px]'
                text={'Change aligner'}
                isDisabled={!canChangeAligner}
                // onClick={formik.handleSubmit}
              />
              {/* <AntdButton
            className='bg-primaryColor text-white font-semibold text-base w-full h-[56px]'
            text={'Change aligner'}
            onClick={() => formik.handleSubmit}
          /> */}
            </div>
          </form>
        </ModalLayout>
      )}
    </>
  )
}

export default ForceAlignerChange

const AlignerSwitch = (props: IForceAlignerProps) => {
  const {startJawType, endJawType, startAlignerNumber, endAlignerNumber} = props
  const startAlignerLabel =
    startAlignerNumber > 0 ? `${startJawType} ${startAlignerNumber}` : 'Current aligner'
  const endAlignerLabel =
    endAlignerNumber > 0 ? `${endJawType} ${endAlignerNumber}` : 'No next aligner'

  return (
    <div className='mt-4'>
      <div className='relative rounded-lg w-full border border-mediumGray flex h-[86px]'>
        <div className='flex flex-col items-center justify-center w-1/2 border-r'>
          <div className='text-[16px] font-medium'>{startAlignerLabel}</div>
          <div className='text-[14px] text-textColor font-medium'>Changed from</div>
        </div>
        <div className='flex flex-col items-center justify-center w-1/2 '>
          <div className='text-[16px] font-medium'>{endAlignerLabel}</div>
          <div className='text-[14px] text-textColor font-medium'>Changed to</div>
        </div>
        <div className='absolute left-1/2 transform -translate-x-1/2 top-1/2 -translate-y-1/2'>
          <BackGroundSVG
            className='w-[48px] h-[48px] bg-lightGray rounded-full'
            svg={SVG_SWITCH_ARROW}
            width='34'
            height='34'
          />
        </div>
      </div>
    </div>
  )
}

const InstructionBoard = () => {
  return (
    <div className='rounded-lg  w-full p-4 border border-textColor flex flex-col mt-5'>
      <div className='text-[16px] text-textColor font-medium'>
        When should you manually change aligner?
      </div>
      <div className='flex gap-2 items-center text-[14px] text-black mt-1 font-medium'>
        <CommonSVG svg={SVG_CORRECT_GREEN} width='16' height='16' />
        <div>Patient changed aligners during a clinic visit</div>
      </div>
      <div className='flex gap-2 items-center text-[14px] text-black font-medium'>
        <CommonSVG svg={SVG_CORRECT_GREEN} width='16' height='16' />
        <div>Patient forgot to update the app</div>
      </div>
    </div>
  )
}
